import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getEmergencySettings,
  updateEmergencySettings,
  getAdminConsultations,
  getCommission,
} from '../../api';
import {
  PageHeader, Table, Pagination, EmptyState, TableSkeleton, StatusBadge, UserCell, AstrologerCell,
} from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import { formatCurrency, formatDateTime, formatDuration } from '../../utils/format';
import { AlertTriangle, Settings2, IndianRupee } from 'lucide-react';
import toast from 'react-hot-toast';

const LIMIT = 20;

export default function EmergencyPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [enabled, setEnabled] = useState(true);
  const [surchargeRupees, setSurchargeRupees] = useState('50');
  const [label, setLabel] = useState('Emergency');

  const { data: settingsPayload, isLoading: settingsLoading } = useQuery({
    queryKey: ['admin-emergency-settings'],
    queryFn: () => getEmergencySettings().then((r) => r.data.data?.settings || r.data.data || {}),
  });

  const { data: commission } = useQuery({
    queryKey: ['admin-commission'],
    queryFn: () => getCommission().then((r) => r.data.data),
  });

  useEffect(() => {
    if (!settingsPayload || typeof settingsPayload !== 'object') return;
    const s = settingsPayload as Record<string, unknown>;
    if (s.enabled !== undefined) setEnabled(s.enabled === true || s.enabled === 'true');
    if (s.surchargePaise !== undefined) setSurchargeRupees(String(Number(s.surchargePaise) / 100));
    if (s.label) setLabel(String(s.label));
  }, [settingsPayload]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-emergency-consults', page],
    queryFn: async () => {
      const r = await getAdminConsultations({ page, limit: LIMIT, isEmergency: true, emergency: '1' });
      return r.data;
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const surchargePaise = Math.round(Number(surchargeRupees) * 100);
      if (!Number.isFinite(surchargePaise) || surchargePaise < 0) {
        throw new Error('Surcharge must be a non-negative number');
      }
      return updateEmergencySettings({
        enabled,
        surchargePaise,
        label: label.trim() || 'Emergency',
      });
    },
    onSuccess: () => {
      toast.success('Emergency costing saved');
      qc.invalidateQueries({ queryKey: ['admin-emergency-settings'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const rows = (data?.data || []).filter((c: any) =>
    c.isEmergency || c.metadata?.emergency || c.priority === 'emergency' || c.type === 'emergency'
  );
  const pagination = data?.pagination;
  const surchargePaise = Math.round(Number(surchargeRupees) * 100) || 0;
  const emergencyCommissionPct = commission?.ratesPercent?.emergency ?? Math.round((commission?.rates?.emergency || 0) * 100);

  return (
    <div className="p-6">
      <PageHeader
        title="Emergency consultations"
        subtitle="Priority sessions — set the flat surcharge users pay on top of the astrologer’s per-minute rate"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div className="card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">Emergency surcharge</p>
          <p className="text-2xl font-bold text-ink mt-1">{formatCurrency(surchargePaise)}</p>
          <p className="text-xs text-ink/50 mt-1">Flat add-on per emergency session</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">Platform commission</p>
          <p className="text-2xl font-bold text-primary-600 mt-1">{emergencyCommissionPct}%</p>
          <p className="text-xs text-ink/50 mt-1">
            From <Link to="/finance/commission" className="text-primary-600 hover:underline">Commission</Link> → Emergency
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">Status</p>
          <p className={`text-2xl font-bold mt-1 ${enabled ? 'text-green-700' : 'text-red-600'}`}>
            {enabled ? 'Enabled' : 'Disabled'}
          </p>
          <p className="text-xs text-ink/50 mt-1">Label in apps: “{label || 'Emergency'}”</p>
        </div>
      </div>

      <div className="card p-5 mb-5">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 size={16} className="text-primary-600" />
          <h3 className="font-semibold text-ink">Emergency session costing</h3>
        </div>
        {settingsLoading ? (
          <TableSkeleton rows={2} cols={3} />
        ) : (
          <>
            <p className="text-sm text-ink/70 mb-4">
              User pays <strong>astrologer ₹/min × minutes</strong> + this <strong>flat surcharge</strong>.
              Platform keeps the emergency commission % of the billed amount.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                Enable emergency sessions
              </label>
              <div>
                <label className="block text-xs font-medium text-ink/60 mb-1">
                  Flat surcharge (₹) <IndianRupee size={12} className="inline" />
                </label>
                <input
                  type="number"
                  min={0}
                  step="1"
                  className="input"
                  value={surchargeRupees}
                  onChange={(e) => setSurchargeRupees(e.target.value)}
                />
                <p className="text-[11px] text-ink/40 mt-1">Stored as {surchargePaise} paise</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-ink/60 mb-1">UI label</label>
                <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save costing'}
              </button>
            </div>
          </>
        )}
      </div>

      <OverviewBar
        items={[
          { label: 'Emergency sessions', value: pagination?.total ?? rows.length, icon: <AlertTriangle size={14} />, color: 'text-orange-600' },
        ]}
      />

      <div className="card mt-4">
        {isLoading ? (
          <div className="p-4"><TableSkeleton rows={5} cols={6} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No emergency consultations yet" icon={<AlertTriangle size={32} />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th className="table-th">User</th>
                  <th className="table-th">Astrologer</th>
                  <th className="table-th">Type</th>
                  <th className="table-th">Status</th>
                  <th className="table-th">Duration</th>
                  <th className="table-th">Amount</th>
                  <th className="table-th">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c: any) => (
                  <tr key={c._id} className="table-row">
                    <td className="table-td"><UserCell entity={c.user} /></td>
                    <td className="table-td"><AstrologerCell entity={c.astrologer} /></td>
                    <td className="table-td text-xs capitalize">{c.type === 'call' ? 'Audio' : c.type}</td>
                    <td className="table-td"><StatusBadge status={c.status} /></td>
                    <td className="table-td">{formatDuration(c.durationSeconds || c.duration || 0)}</td>
                    <td className="table-td font-semibold">{formatCurrency(c.totalAmount || c.amount || 0)}</td>
                    <td className="table-td">
                      <Link to={`/consultations/${c._id}`} className="text-primary-600 hover:underline text-xs">
                        {formatDateTime(c.createdAt)}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {pagination && (
              <div className="p-4">
                <Pagination
                  page={pagination.page || page}
                  pages={pagination.pages || 1}
                  total={pagination.total || rows.length}
                  limit={pagination.limit || LIMIT}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
